import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import debounce from 'transmute/debounce';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { View } from 'foundations-components/components/View';
import { MIN_SEARCH_LENGTH, SEARCH_LENGTH_LIMIT } from 'customer-data-objects/search/ElasticSearchConstants';
import I18n from 'I18n';
// @ts-expect-error: no types
import CjkCharacterRegExp from 'I18n/constants/regex/CJK';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { useLowercaseCrmObjectTypeName } from 'framework-listing-lib/internal/hooks/useCrmObjectTypeName';
import { useIsSearchInputDisabled, useLegacySearchInput, useOnQueryChange, useSearchInputPlaceholder, useSearchInputWidth } from 'framework-listing-lib/internal/hooks/useSearchInputProps';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
const SEARCH_INPUT_WIDTH = 250;
const DEBOUNCE_MS = 500;

// copied from https://git.hubteam.com/HubSpot/CRM/blob/a89d6bb87c24005be6eb9da5d13716da20897a76/crm_data/static/js/elasticSearch/ElasticSearchValidation.js
function isCjkCharacterContained(str = '') {
  return Boolean(str) && CjkCharacterRegExp.test(str);
}
function isOfMinSearchLength(query) {
  return !!query && (query.length >= MIN_SEARCH_LENGTH || isCjkCharacterContained(query));
}
function isTooLong(query = '') {
  if (!query || typeof query !== 'string') {
    return false;
  }
  const hasTooManyCharacters = query.length > SEARCH_LENGTH_LIMIT;
  if (hasTooManyCharacters) {
    return {
      type: 'characters',
      limit: SEARCH_LENGTH_LIMIT
    };
  }
  return false;
}
function getValidationMessage(value) {
  if (!value) {
    return null;
  }
  if (!isOfMinSearchLength(value)) {
    return I18n.text('salesUI.UISearchInput.minimumSearchMessage', {
      count: MIN_SEARCH_LENGTH - value.length
    });
  }
  return null;
}
export default function CrmListingSearchInput() {
  const hasLegacySearchInput = useLegacySearchInput();
  const onQueryChange = useOnQueryChange();
  const searchInputPlaceholder = useSearchInputPlaceholder();
  const searchInputWidth = useSearchInputWidth();
  const isSearchInputDisabled = useIsSearchInputDisabled();
  const {
    lowerCaseObjectTypeNamePlural,
    capitalizedObjectTypeNamePlural
  } = useLowercaseCrmObjectTypeName();
  const {
    filters,
    onUpdateFilters
  } = useCrmObjectTypeListingFilters();
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const {
    pagination
  } = filters;
  const [searchQueryTerm, setSearchQueryTerm] = useState(filters.query);
  const typingLock = useRef(false);
  const setQueryTermDebounced = useMemo(() => debounce(DEBOUNCE_MS, filter => {
    onUpdateFilters(filter);
    if (onQueryChange) {
      onQueryChange(filter.query || '');
    }
    trackCrmTableInteraction({
      action: 'filter-objects-by-query'
    });
    typingLock.current = false;
  }), [onQueryChange, onUpdateFilters, trackCrmTableInteraction]);
  const handleChange = useCallback(event => {
    typingLock.current = true;
    const {
      value
    } = event.target;
    setSearchQueryTerm(value);
    if (isOfMinSearchLength(value) && !isTooLong(value) || value === '') {
      setQueryTermDebounced({
        query: value,
        pagination: {
          count: pagination.count,
          offset: 0
        }
      });
    }
  }, [pagination.count, setQueryTermDebounced]);
  const maxLengthLimitValidationMeta = useMemo(() => {
    const {
      limit,
      type
    } = isTooLong(searchQueryTerm) || {};
    if (type && limit) {
      return I18n.text('frameworkListingLib.searchInput.limitExceeded', {
        limit,
        type
      });
    }
  }, [searchQueryTerm]);
  const placeholder = useMemo(() => searchInputPlaceholder || I18n.text('frameworkListingLib.searchInput.placeholder', {
    objectTypeName: I18n.SafeString(lowerCaseObjectTypeNamePlural),
    capitalizedObjectTypeName: I18n.SafeString(capitalizedObjectTypeNamePlural)
  }), [lowerCaseObjectTypeNamePlural, capitalizedObjectTypeNamePlural, searchInputPlaceholder]);
  useEffect(() => {
    if (filters.query !== searchQueryTerm && !typingLock.current) {
      setSearchQueryTerm(filters.query);
      onQueryChange === null || onQueryChange === void 0 || onQueryChange(filters.query);
    }
  }, [filters.query, onQueryChange, searchQueryTerm]);
  const commonProps = {
    disabled: isSearchInputDisabled,
    getValidationMessage,
    onChange: handleChange,
    placeholder,
    value: searchQueryTerm
  };
  if (hasLegacySearchInput) {
    return /*#__PURE__*/_jsx(View, {
      inlineSize: searchInputWidth || `${SEARCH_INPUT_WIDTH}px`,
      children: /*#__PURE__*/_jsx(Tooltip, {
        title: maxLengthLimitValidationMeta,
        open: !!maxLengthLimitValidationMeta,
        disabled: !maxLengthLimitValidationMeta,
        children: /*#__PURE__*/_jsx(FormControl, {
          "aria-label": placeholder,
          marginInlineEnd: "300",
          children: /*#__PURE__*/_jsx(SearchInput, Object.assign({
            "data-test-id": `crm-listing-search-input-legacy`
          }, commonProps))
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(View, {
    inlineSize: searchInputWidth || `${SEARCH_INPUT_WIDTH}px`,
    children: /*#__PURE__*/_jsx(Tooltip, {
      title: maxLengthLimitValidationMeta,
      open: !!maxLengthLimitValidationMeta,
      disabled: !maxLengthLimitValidationMeta,
      children: /*#__PURE__*/_jsx(FormControl, {
        "aria-label": placeholder,
        children: /*#__PURE__*/_jsx(SearchInput, Object.assign({
          "data-test-id": `crm-listing-search-input`,
          use: 'on-dark',
          blockSize: "30px"
        }, commonProps))
      })
    })
  });
}